import { FabricObject, util } from 'fabric';
import {
  initAllPills,
  type CanvasJSONLikeObject,
  type PolymorphicBinding,
  type PolymorphicPieceOverride
} from '@doer/frontend-components/creativity-editor';
import type { Binder } from './binder';
import type { RoutedFormData } from '../../../../types/templates';

// helpers

/** Every top-level pieceId across all variants (used to know what this binding "owns" in the canvas). */
const topLevelPieceIds = (binding: PolymorphicBinding): Set<string> => {
  const ids = new Set<string>();
  for (const variant of Object.values(binding.variants)) {
    for (const piece of variant.pieces) {
      if (typeof piece.pieceId === 'string') ids.add(piece.pieceId);
    }
  }
  return ids;
};

const variantPieceIds = (binding: PolymorphicBinding, variantKey: string | undefined): Set<string> => {
  const ids = new Set<string>();
  if (!variantKey) return ids;
  const variant = binding.variants[variantKey];
  if (!variant) return ids;
  for (const piece of variant.pieces) {
    if (typeof piece.pieceId === 'string') ids.add(piece.pieceId);
  }
  return ids;
};

const readActiveVariant = (formData: RoutedFormData, binding: PolymorphicBinding): string | undefined => {
  const raw = formData[binding.fieldKey];
  if (raw && typeof raw === 'object') {
    const v = (raw as { variant?: unknown }).variant;
    if (typeof v === 'string') return v;
  }
  return binding.defaultVariant;
};

type PolymorphicValueShape = { variant?: string; values?: Record<string, unknown> };
const readPolymorphicValue = (raw: unknown): PolymorphicValueShape =>
  raw != null && typeof raw === 'object' ? (raw as PolymorphicValueShape) : {};

const OVERRIDE_FIELDS: (keyof PolymorphicPieceOverride)[] = [
  'left',
  'top',
  'originX',
  'originY',
  'scaleX',
  'scaleY',
  'angle',
  'width',
  'height'
];

function applyPositionOverrides(
  pieces: CanvasJSONLikeObject[],
  binding: PolymorphicBinding,
  resolutionId: string | undefined
): CanvasJSONLikeObject[] {
  const overrides = resolutionId ? binding.positionsByResolution?.[resolutionId] : undefined;
  if (!overrides) return pieces;
  return pieces.map(piece => {
    const id = piece.pieceId;
    if (typeof id !== 'string') return piece;
    const override = overrides[id];
    if (!override) return piece;
    const merged: CanvasJSONLikeObject = { ...piece };
    for (const field of OVERRIDE_FIELDS) {
      if (override[field] !== undefined) merged[field] = override[field];
    }
    return merged;
  });
}

// canvas-side mount

async function mountOnCanvas(
  canvas: Parameters<NonNullable<Binder<PolymorphicBinding>['preMountCanvas']>>[0],
  binding: PolymorphicBinding,
  activeVariant: string | undefined,
  resolutionId: string | undefined
): Promise<void> {
  const owned = topLevelPieceIds(binding);
  const active = variantPieceIds(binding, activeVariant);

  const toRemove = canvas.getObjects().filter(obj => {
    const id = obj.pieceId;
    return typeof id === 'string' && owned.has(id) && !active.has(id);
  });
  toRemove.forEach(obj => canvas.remove(obj));

  if (!activeVariant) return;

  const present = new Set(
    canvas
      .getObjects()
      .map(o => o.pieceId)
      .filter((id): id is string => typeof id === 'string')
  );
  const candidatePieces = applyPositionOverrides(binding.variants[activeVariant].pieces, binding, resolutionId);
  const missing = candidatePieces.filter(p => typeof p.pieceId === 'string' && !present.has(p.pieceId));

  if (missing.length > 0) {
    const enlivened = (await util.enlivenObjects(missing)) as FabricObject[];
    enlivened.forEach(obj => canvas.add(obj));
  }

  // Idempotent — wires both the pieces we just enlivened and any already in the canvas.
  initAllPills(canvas);
}

// JSON-side mount

function mountInJson(
  objects: CanvasJSONLikeObject[],
  binding: PolymorphicBinding,
  activeVariant: string | undefined,
  resolutionId: string | undefined
): CanvasJSONLikeObject[] {
  const owned = topLevelPieceIds(binding);
  const active = variantPieceIds(binding, activeVariant);

  const filtered = objects.filter(obj => {
    const id = obj.pieceId;
    return typeof id !== 'string' || !owned.has(id) || active.has(id);
  });

  if (!activeVariant) return filtered;

  const present = new Set(filtered.map(o => o.pieceId).filter((id): id is string => typeof id === 'string'));
  const candidatePieces = applyPositionOverrides(binding.variants[activeVariant].pieces, binding, resolutionId);
  const missing = candidatePieces.filter(p => typeof p.pieceId === 'string' && !present.has(p.pieceId));

  return missing.length === 0 ? filtered : [...filtered, ...missing];
}

// Binder

// undefined means "no variant active" → mount* unmounts everything (the binding's pieces go away).
const activeVariantFor = (
  binding: PolymorphicBinding,
  formData: RoutedFormData,
  resolutionId: string | undefined
): string | undefined => {
  if (binding.applicableResolutions && resolutionId && !binding.applicableResolutions.includes(resolutionId)) {
    return undefined;
  }
  return readActiveVariant(formData, binding);
};

export const polymorphicBinder: Binder<PolymorphicBinding> = {
  preMountCanvas(canvas, binding, formData, resolutionId) {
    return mountOnCanvas(canvas, binding, activeVariantFor(binding, formData, resolutionId), resolutionId);
  },

  preMountJson(objects, binding, formData, resolutionId) {
    return mountInJson(objects, binding, activeVariantFor(binding, formData, resolutionId), resolutionId);
  },

  resolveTargets(binding, formData, defaults) {
    const fromForm = readPolymorphicValue(formData[binding.fieldKey]);
    const fromDefaults = readPolymorphicValue(defaults[binding.fieldKey]);
    const activeVariant = fromForm.variant ?? fromDefaults.variant ?? binding.defaultVariant;
    const values = fromForm.values ?? fromDefaults.values ?? {};
    const variant = binding.variants[activeVariant];
    if (!variant) return [];

    return variant.bindings.map(b => ({
      pieceId: b.pieceId,
      bindingType: b.bindingType,
      value: values[b.from]
    }));
  },

  pieceMapEntries(binding) {
    const entries: Array<[string, ReturnType<Binder<PolymorphicBinding>['pieceMapEntries']>[number][1]]> = [];
    for (const [variantKey, variant] of Object.entries(binding.variants)) {
      for (const b of variant.bindings) {
        entries.push([
          b.pieceId,
          {
            fieldKey: binding.fieldKey,
            kind: 'polymorphic',
            bindingType: b.bindingType,
            variant: variantKey,
            from: b.from
          }
        ]);
      }
    }
    return entries;
  },

  computeNextFieldValue(entry, formData, newValue) {
    if (entry.kind !== 'polymorphic') return newValue;
    const current = formData[entry.fieldKey];
    const base = current != null && typeof current === 'object' ? (current as Record<string, unknown>) : {};
    const prevValues = (base.values as Record<string, unknown>) ?? {};
    return { ...base, variant: entry.variant, values: { ...prevValues, [entry.from]: newValue } };
  }
};
